
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { pricingPlans } from "@/config/pricing";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Shield, ArrowRight, Sparkles } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { CSOLoadingSpinner } from "@/components/ui/cso-loading-spinner";
import { useReferralTracking } from "@/components/referral/ReferralTrackingProvider";
import { Badge } from "@/components/ui/badge";
import { FreeTrialDialog } from "@/components/trial/FreeTrialDialog";

export const Pricing = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [showTrialDialog, setShowTrialDialog] = useState(false);
  const { getReferralCode } = useReferralTracking();

  const handleCheckout = async (priceId: string, subscriptionType: string) => {
    try {
      setLoading(priceId);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to continue with your purchase.",
          variant: "destructive",
        });
        navigate("/auth?mode=signin");
        return;
      }

      // Get referral code if exists
      const referralCode = getReferralCode();
      console.log('Using referral code for checkout:', referralCode);

      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          priceId, 
          subscriptionType,
          referralCode
        }
      });

      if (error) {
        console.error('Checkout error:', error);
        throw error;
      }

      if (data?.url) {
        // Always open in same window to avoid losing session
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: "Error",
        description: "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleStartFreeTrial = () => {
    setShowTrialDialog(true);
  };

  return (
    <>
      <section id="pricing" className="container grid items-center justify-center gap-6 pb-8 pt-6 md:py-10">
        <div className="mx-auto max-w-[980px] text-center">
          <h2 className="heading-lg text-corporate-primary mb-4">
            Investment That Drives Results
          </h2>
          <p className="body-lg text-corporate-secondary max-w-3xl mx-auto mb-6">
            ROI-focused pricing designed for executives who measure success in outcomes, not features.
          </p>
          
          {/* Value Propositions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <div className="bg-trust-background border border-trust-border rounded-lg p-4">
              <div className="text-2xl font-bold text-corporate-primary mb-1">AI-Powered</div>
              <div className="text-sm text-corporate-secondary">Strategic planning automation</div>
            </div>
            <div className="bg-trust-background border border-trust-border rounded-lg p-4">
              <div className="text-2xl font-bold text-corporate-primary mb-1">Enterprise</div>
              <div className="text-sm text-corporate-secondary">Security & compliance ready</div>
            </div>
            <div className="bg-trust-background border border-trust-border rounded-lg p-4">
              <div className="text-2xl font-bold text-corporate-primary mb-1">24/7</div>
              <div className="text-sm text-corporate-secondary">Available when you need it</div>
            </div>
          </div>
        </div>

        {/* Free Trial Section */}
        <div className="max-w-md mx-auto mb-6 px-4 sm:px-0">
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-blue-800">Try Before You Buy</h3>
              </div>
              <p className="text-sm text-blue-600 mb-4">
                Get 24 hours of full access to all premium features
              </p>
              <Button 
                onClick={handleStartFreeTrial}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Start Free Trial
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* CSO Agent Bot on Telegram */}
        <div className="max-w-md mx-auto mb-6 px-4 sm:px-0">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 relative">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
              <Badge className="bg-green-600 text-white px-3 py-1 text-xs">Free</Badge>
            </div>
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img 
                  src="/lovable-uploads/ddb58a86-8540-4e28-afc2-68b5446e0649.png" 
                  alt="CSO Agent Logo" 
                  className="h-8 w-8"
                />
                <h3 className="font-semibold text-blue-800">CSO Agent Bot on Telegram</h3>
              </div>
              <p className="text-sm text-blue-600 mb-4">
                Free strategic business conversations with CSO Agent
              </p>
              <div className="text-left text-sm text-gray-600 space-y-1 mb-4">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Free chat conversations</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Strategic business advice</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500" />
                  <span>Available 24/7</span>
                </div>
              </div>
              <Button 
                onClick={() => window.open('https://t.me/CSOAgent_Bot', '_blank')}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                Chat on Telegram
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid w-full grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {pricingPlans?.map((plan) => (
            <Card
              key={plan.id}
              className={cn(
                "relative border-2 hover:border-cso-600 transition-colors h-full flex flex-col",
                plan.isRecommended && "border-cso-600"
              )}
            >
              {plan.label && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <Badge className="bg-cso-600 text-white px-3 py-1 text-xs whitespace-nowrap">
                    {plan.label}
                  </Badge>
                </div>
              )}
              
               <CardHeader className="flex-shrink-0">
                 <CardTitle className="text-xl font-semibold text-corporate-primary">{plan.name}</CardTitle>
                 <div className="mb-4">
                   <span className="text-3xl font-bold text-corporate-primary">${plan.price}</span>
                   <span className="text-corporate-secondary">/{plan.interval}</span>
                 </div>
                 <p className="text-corporate-secondary text-sm min-h-[2.5rem] flex items-center">{plan.description}</p>
                 
                  {/* Value highlights for each plan */}
                  <div className="bg-trust-background rounded-md p-3 mt-3">
                    <div className="text-xs font-medium text-corporate-primary mb-1">Key Benefit</div>
                    <div className="text-sm text-corporate-secondary">
                      {plan.name === 'Monthly' && 'Perfect for individual executives getting started'}
                      {plan.name === 'Team' && 'Collaborative strategic planning for teams'}
                      {plan.name === 'Enterprise' && 'Complete organizational transformation'}
                      {plan.name === 'Lifetime' && 'One-time investment, lifetime access to all features'}
                    </div>
                  </div>
               </CardHeader>
              
              <CardContent className="flex-grow flex flex-col">
                <ul className="mb-6 space-y-2 flex-grow">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm text-gray-700">
                      <Check className="h-4 w-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-auto">
                  <Separator className="my-4" />
                   <Button
                     className="w-full bg-corporate-primary hover:bg-corporate-secondary text-white font-medium"
                     onClick={() => handleCheckout(plan.priceId, plan.type)}
                     disabled={loading === plan.priceId}
                   >
                     {loading === plan.priceId ? (
                       <>
                         <CSOLoadingSpinner size="sm" />
                         Processing
                       </>
                      ) : (
                        "Start Now"
                      )}
                   </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <div className="border rounded-md p-4 bg-gray-50 max-w-md mx-auto mx-4 sm:mx-auto">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-green-500" />
            <p className="text-sm font-medium">Secure payments via Stripe</p>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Your payment information is encrypted and securely processed.
          </p>
        </div>
      </section>

      <FreeTrialDialog 
        isOpen={showTrialDialog} 
        onClose={() => setShowTrialDialog(false)} 
      />
    </>
  );
};
